import { z } from 'zod/v3';
import { AlgorithmJoinFormDataSchema } from '../algorithms/joins/schema/formData/AlgorithmJoinFormDataSchema';
import { AlgorithmMappingQueryDtoSchema } from '../algorithms/mappings/schema/AlgorithmMappingQueryDtoSchema';
import { AlgorithmExtendedSchema } from '../algorithms/schema/AlgorithmExtendedSchema';
import type { AlgorithmExtendedType } from '../algorithms/schema/types';
import { AlgorithmSourceFormDataSchema } from '../algorithms/sources/schema/AlgorithmSourceFormDataSchema';
import { AlgorithmSourcesConfidentialityRuleFormDataSchema } from '../algorithms/sources/schema/AlgorithmSourcesFormDataSchema';
import { AlgorithmTargetFormDataSchema } from '../algorithms/targets/schema/AlgorithmTargetFormDataSchema';

export type AlgorithmMetadataSnapshot =
  | { status: 'complete'; data: AlgorithmExtendedType }
  | { status: 'recovered'; data: AlgorithmExtendedType }
  | { status: 'unavailable' };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const recoverArray = (values: unknown, schema: z.ZodTypeAny): unknown[] => {
  if (!Array.isArray(values)) return [];

  return values.filter((value) => schema.safeParse(value).success);
};

const normalizeSource = (value: unknown): unknown => {
  if (!isRecord(value) || !isRecord(value.dataObjectDescription)) return value;

  return {
    ...value,
    dataObjectDescription: {
      outputTypeCode: null,
      ...value.dataObjectDescription,
    },
  };
};

const normalizeAlgorithmSnapshot = (value: unknown): unknown => {
  if (!isRecord(value)) return value;

  const validPeriod = Array.isArray(value.validPeriod) ? value.validPeriod : [];
  const sources = Array.isArray(value.sources)
    ? value.sources.map(normalizeSource)
    : value.sources;

  return {
    ...value,
    validFrom: value.validFrom ?? validPeriod[0] ?? null,
    validTo: value.validTo ?? validPeriod[1] ?? null,
    execAfterAggregation: value.execAfterAggregation ?? null,
    sources,
  };
};

const recoverAlgorithmSnapshot = (
  value: unknown,
  schemaVersion: number,
): unknown => {
  const normalized =
    schemaVersion === 1 ? normalizeAlgorithmSnapshot(value) : value;
  if (!isRecord(normalized)) return normalized;

  const confidentialityResult =
    AlgorithmSourcesConfidentialityRuleFormDataSchema.safeParse(
      normalized.confidentialityRule,
    );
  const confidentialityRule = confidentialityResult.success
    ? normalized.confidentialityRule
    : undefined;

  return {
    ...normalized,
    confidentialityRule,
    sources: recoverArray(normalized.sources, AlgorithmSourceFormDataSchema),
    mappings: recoverArray(normalized.mappings, AlgorithmMappingQueryDtoSchema),
    joins: recoverArray(normalized.joins, AlgorithmJoinFormDataSchema),
    targets: recoverArray(normalized.targets, AlgorithmTargetFormDataSchema),
  };
};

/**
 * Parses the algorithm metadata snapshot and returns an object containing the status and data.
 * Uses best-effort recovery to handle invalid or incomplete data.
 * @param raw - The raw algorithm metadata snapshot to parse.
 * @param schemaVersion - The schema version of the algorithm metadata snapshot.
 * @returns An object containing the status and data of the parsed algorithm metadata snapshot.
 */
export const parseAlgorithmMetadataSnapshot = (
  raw: unknown,
  schemaVersion: number,
): AlgorithmMetadataSnapshot => {
  const currentResult = AlgorithmExtendedSchema.safeParse(raw);
  if (currentResult.success) {
    return { status: 'complete', data: currentResult.data };
  }

  const recovered = recoverAlgorithmSnapshot(raw, schemaVersion);
  const finalResult = AlgorithmExtendedSchema.safeParse(recovered);
  if (finalResult.success) {
    return {
      status: 'recovered',
      data: finalResult.data,
    };
  }

  return { status: 'unavailable' };
};
